import { EntityType } from '../entities/common';
import * as Metadata from '../entities/Metadata';
import { Resource } from '../store/ResourcesListStore';
import { FieldsState } from '../store/fields';

export type TimephasedFieldConfig = {
    entityType: EntityType.TimephasedRate | EntityType.TimephasedCost | EntityType.TimephasedMaxUnits | EntityType.TimephasedNonProjectWork;
    collectionKey: keyof Resource;
    supportsCostRateTable: boolean;
};

export function getTimephasedEntityType(fieldName: string): TimephasedFieldConfig['entityType'] | undefined {
    if (fieldName === 'StandardRate' || fieldName === 'OvertimeRate' || fieldName === 'ChargePerUse') {
        return EntityType.TimephasedRate;
    }
    if (fieldName === 'StandardCost' || fieldName === 'OvertimeCost' || fieldName === 'CostPerUse') {
        return EntityType.TimephasedCost;
    }
    if (fieldName === 'MaxUnitsPct') {
        return EntityType.TimephasedMaxUnits;
    }
    if (fieldName === 'NonProjectWorkPct') {
        return EntityType.TimephasedNonProjectWork;
    }
    return undefined;
}

export function getTimephasedFieldConfig(fieldName: string): TimephasedFieldConfig | null {
    const entityType = getTimephasedEntityType(fieldName);
    if (!entityType) {
        return null;
    }

    const configMap: Record<TimephasedFieldConfig['entityType'], Omit<TimephasedFieldConfig, 'entityType'>> = {
        [EntityType.TimephasedRate]: {
            collectionKey: 'timephasedRates',
            supportsCostRateTable: true
        },
        [EntityType.TimephasedCost]: {
            collectionKey: 'timephasedCosts',
            supportsCostRateTable: true
        },
        [EntityType.TimephasedMaxUnits]: {
            collectionKey: 'timephasedMaxUnits',
            supportsCostRateTable: false
        },
        [EntityType.TimephasedNonProjectWork]: {
            collectionKey: 'timephasedNonProjectWork',
            supportsCostRateTable: false
        }
    };

    return {
        entityType,
        ...configMap[entityType]
    };
}

export function resolveTimephasedFieldFromStore(
    fields: Record<string, FieldsState> | undefined,
    field: Metadata.Field
): Metadata.Field | undefined {
    const config = getTimephasedFieldConfig(field.name);
    if (!config?.entityType) {
        return undefined;
    }

    const store = fields?.[config.entityType];
    const ids = store?.allIds as string[] | undefined;
    const byId = store?.byId as Record<string, Metadata.Field> | undefined;

    if (!ids || !byId) {
        return undefined;
    }

    for (const id of ids) {
        const f = byId[id];
        if (f?.name === field.name) {
            return f;
        }
    }

    return undefined;
}
